import {t} from "@lingui/core/macro";
import gql from "graphql-tag";

import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

export type JobListing = {
    title: string;
    location: string;
    url: string;
};

export type JobListingSection = {
    name: string;
    jobs: Array<JobListing>;
};

type Data = {
    careers?: {
        departments: Array<JobListingSection>;
    };
};

const CAREERS_PAGE_QUERY: GqlOperation<Data> = gqlOp(gql`
    query careersPageQuery {
        careers {
            departments {
                name
                jobs {
                    title
                    location
                    url
                }
            }
        }
    }
`);

export const fetchCareersData = async (
    gqlClient: ApolloGqlClient,
): Promise<Array<JobListingSection> | null | undefined> => {
    const result = await gqlClient.query(CAREERS_PAGE_QUERY, {
        fetchPolicy: "network-only",
    });

    return result.data?.careers?.departments ?? null;
};

export const formatCareersData = (
    careers: Array<JobListingSection>,
    queryParams: Record<string, string>,
): Array<JobListingSection> => {
    const data = [...careers];

    // KA India goes second to last
    const kaIndiaIndex = data.findIndex(
        (department) => department.name === "Khan Academy India",
    );
    if (kaIndiaIndex >= 0) {
        data.splice(kaIndiaIndex, 1);
    }

    // Sort alphabetically and add KA India to the bottom
    data.sort((a, b) => a.name.localeCompare(b.name));

    if (kaIndiaIndex >= 0) {
        data.push(careers[kaIndiaIndex]);
    }

    // Remove KA Lab school and add a link their careers page at the vey end
    const khanLabSchoolDepartment = {
        name: "Khan Lab School",
        jobs: [
            {
                url: "https://www.khanlabschool.org/Careers",
                location: "",
                title: t`Please see the KLS careers page`,
            },
        ],
    } as const;
    const kaLabSchoolIndex = data.findIndex(
        (department) => department.name === "Khan Lab School",
    );
    if (kaLabSchoolIndex >= 0) {
        data.splice(kaLabSchoolIndex, 1);
    }
    // @ts-expect-error - TS2345 - Argument of type '{ readonly name: "Khan Lab School"; readonly jobs: readonly [{ readonly url: string; readonly location: ""; readonly title: string; }]; }' is not assignable to parameter of type 'JobListingSection'.
    data.push(khanLabSchoolDepartment);

    // Add the query parameters to the url to support greenhouse tracking
    // support.greenhouse.io/hc/en-us/articles/216904366-External-Job-Board-Tracking-Links-Overview
    return data
        .filter((department) => department.jobs.length > 0)
        .map((department) => {
            return {
                ...department,

                jobs: department.jobs.map((job) => {
                    const url = new URL(job.url);
                    Object.keys(queryParams).forEach((key) => {
                        url.searchParams.append(key, queryParams[key]);
                    });
                    return {...job, url: url.toString()};
                }),
            };
        });
};
